// src/components/shifts/ShiftCard.tsx

import React from "react";
import {
    ActionIcon,
    Badge,
    Group,
    Paper,
    Progress,
    Stack,
    Text,
} from "@mantine/core";
import { IconCopy, IconEdit, IconEye, IconTrash } from "@tabler/icons-react";
import { formatTime } from "../../utils/date";
import "./ShiftCard.css";

export type ShiftDto = {
    shiftsID: number;
    functionID: number;
    functionName: string;
    eventID: number;

    shiftsName: string;
    startTime: string;
    endTime: string;

    maxParticipants: number;
    waitlist: boolean;
    ticketID?: number | null;
    endsOneHourAfterPlay: boolean;

    registeredCount: number;
    waitlistCount: number;
};

export function getStatus(s: ShiftDto): "Ledig" | "Delvist" | "Fuldt" {
    if ((s.registeredCount ?? 0) >= s.maxParticipants) return "Fuldt";
    if ((s.registeredCount ?? 0) > 0) return "Delvist";
    return "Ledig";
}

function StatusBadge({
    status,
}: {
    status: "Ledig" | "Delvist" | "Fuldt";
}) {
    if (status === "Fuldt") return <Badge color="blue">Fuldt</Badge>;
    if (status === "Delvist") return <Badge color="yellow">Delvist</Badge>;
    return <Badge color="green">Ledig</Badge>;
}

export default function ShiftCard({
    shift,
    onView,
    onDelete,
    onEdit,
    onCopy,
}: {
    shift: ShiftDto;
    onView: (s: ShiftDto) => void;
    onDelete?: (id: number) => void;
    onEdit?: (s: ShiftDto) => void;
    onCopy?: (s: ShiftDto) => void;
}) {
    const st = getStatus(shift);

    const accent =
        st === "Fuldt"
            ? "rgba(59, 130, 246, 0.95)"
            : st === "Delvist"
                ? "rgba(245, 158, 11, 0.95)"
                : "rgba(34, 197, 94, 0.95)";

    const start = new Date(shift.startTime);
    const end = new Date(shift.endTime);

    const day = start.toLocaleDateString("da-DK", {
        weekday: "short",
    });

    const date = start.toLocaleDateString("da-DK", {
        day: "2-digit",
        month: "short",
    });

    const reg = shift.registeredCount ?? 0;
    const max = shift.maxParticipants ?? 0;

    const pct =
        max > 0
            ? Math.min(100, Math.round((reg / max) * 100))
            : 0;

    const timeRange = `${formatTime(start)} – ${formatTime(end)}`;

    const overlayText =
        shift.waitlistCount > 0
            ? `${reg} / ${max} · venteliste ${shift.waitlistCount}`
            : `${reg} / ${max}`;

    return (
        <Paper
            withBorder
            p="md"
            radius="md"
            className="shift-card"
            style={{
                position: "relative",
                overflow: "hidden",
                transition: "transform .12s ease, box-shadow .12s ease",
            }}
            onMouseEnter={(e) =>
                (e.currentTarget.style.transform = "translateY(-1px)")
            }
            onMouseLeave={(e) =>
                (e.currentTarget.style.transform = "translateY(0px)")
            }
        >
            {/* venstre farvebar */}
            <div
                style={{
                    position: "absolute",
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: 6,
                    background: accent,
                }}
            />

            <Group
                justify="space-between"
                align="flex-start"
                wrap="nowrap"
                className="shift-card-main"
            >
                {/* venstre indhold */}
                <Group
                    align="flex-start"
                    gap="md"
                    className="shift-card-left"
                >
                    {/* DATO BLOK */}
                    <Paper
                        withBorder
                        radius="md"
                        p="sm"
                        className="shift-card-date"
                    >
                        <Text size="xs" c="dimmed" tt="uppercase">
                            {day}
                        </Text>

                        <Text fw={800} size="lg">
                            {date}
                        </Text>

                        <Text size="sm" fw={700}>
                            {formatTime(start)}
                        </Text>
                    </Paper>

                    {/* CONTENT */}
                    <Stack
                        gap={6}
                        className="shift-card-content"
                    >
                        <Group gap="xs" wrap="wrap">
                            <Text
                                fw={800}
                                className="shift-card-title"
                            >
                                {shift.shiftsName}
                            </Text>

                            <StatusBadge status={st} />

                            {shift.waitlistCount > 0 && (
                                <Badge
                                    variant="light"
                                    color="yellow"
                                >
                                    Venteliste
                                </Badge>
                            )}
                        </Group>

                        <Text
                            c="dimmed"
                            size="sm"
                            style={{
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                            }}
                        >
                            {timeRange} · {shift.functionName}
                        </Text>

                        {/* PROGRESS */}
                        <div style={{ marginTop: 2 }}>
                            <div style={{ position: "relative" }}>
                                <Progress
                                    value={pct}
                                    radius="xl"
                                    size="md"
                                    styles={{
                                        root: {
                                            height: 18,
                                        },
                                        section: {
                                            transition:
                                                "width .25s ease",
                                        },
                                    }}
                                />

                                <div
                                    style={{
                                        position: "absolute",
                                        inset: 0,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        fontSize: 12,
                                        fontWeight: 700,
                                        color:
                                            pct > 55
                                                ? "white"
                                                : "#495057",
                                        pointerEvents: "none",
                                        userSelect: "none",
                                    }}
                                >
                                    {overlayText}
                                </div>
                            </div>
                        </div>
                    </Stack>
                </Group>

                {/* ACTIONS */}
                <Group
                    gap={8}
                    wrap="nowrap"
                    className="shift-card-actions"
                >
                    <ActionIcon
                        variant="light"
                        radius="md"
                        size="lg"
                        aria-label="Se tilmeldte"
                        onClick={() => onView(shift)}
                    >
                        <IconEye size={16} />
                    </ActionIcon>

                    {onEdit && (
                        <ActionIcon
                            variant="light"
                            radius="md"
                            size="lg"
                            aria-label="Rediger"
                            onClick={() => onEdit?.(shift)}
                        >
                            <IconEdit size={16} />
                        </ActionIcon>
                    )}

                    {onCopy && (
                        <ActionIcon
                            variant="light"
                            radius="md"
                            size="lg"
                            aria-label="Kopier"
                            onClick={() => onCopy?.(shift)}
                        >
                            <IconCopy size={16} />
                        </ActionIcon>
                    )}

                    {onDelete && (
                        <ActionIcon
                            color="red"
                            variant="light"
                            radius="md"
                            size="lg"
                            aria-label="Slet"
                            onClick={() =>
                                onDelete(shift.shiftsID)
                            }
                        >
                            <IconTrash size={16} />
                        </ActionIcon>
                    )}
                </Group>
            </Group>
        </Paper>
    );
}